/**
 * Choice display text for a SurveyJS survey, resolved by survey-core.
 *
 * Choice values stay the stored answer identities. The text a person should
 * read comes from the SurveyJS model (locale, carry-forward, matrix columns,
 * dynamic-panel templates), not from a hand-rolled walk of the JSON.
 *
 * Identity labels are omitted: when the resolved text equals the value, the
 * panel already shows that value, so the map does not repeat it.
 *
 * `choicesByUrl` is not fetched. SurveyJS loads those choices with
 * XMLHttpRequest while the model is constructed, which this build does not
 * perform (and which throws where XMLHttpRequest is missing). The property is
 * removed before `new Model`, static choices on the same question are still
 * resolved, and an answer with no label is shown as the stored raw value.
 *
 * Matrix row text and column text live in separate maps. Nested column and
 * dynamic-panel questions keep their own maps, so a shared value cannot
 * overwrite a label from another column.
 *
 * Open-text fields (text, comment, multipletext items), including top-level
 * ones, are marked `isOpenText`. An empty `values` map is not that marker:
 * identity choices (`text === value`) are omitted and also leave `values`
 * empty, and those codes still use the raw-choice fallback.
 *
 * SurveyJS "Other" free text is a nested open-text child keyed by
 * `otherItem.value` (usually `other`). The parent choice map stays for
 * real choices. The renderer does not treat the key `other`, an object
 * shape, or an empty value map as that child.
 *
 * The canonical key is the field name. A display title is only an alias for
 * that same payload. An alias never merges two different fields, so a column
 * title that matches another column's name cannot copy choice labels onto
 * an open-text cell.
 *
 * One-shot pure function. Not an LLM prompt.
 */

import { ItemValue, Model, Question } from 'survey-core';

export interface QuestionChoiceLabels {
  /** Choice, rating, boolean, and matrix-row value → authored text. */
  values: Record<string, string>;
  /**
   * Single-choice matrix column value → text.
   * Kept apart from `values` so a row and a column can share a value.
   */
  columns?: Record<string, string>;
  /**
   * Nested column, dynamic-panel question, or multipletext field.
   * Keyed by the field name. A display title may alias that same payload
   * when the alias is not already another field's name.
   */
  nested?: Record<string, QuestionChoiceLabels>;
  /** Localized title when it differs from the question or column name. */
  title?: string;
  /**
   * The stored scalar is typed text, not a choice code.
   * Empty `values` is not enough: identity choices omit their labels too.
   */
  isOpenText?: boolean;
  /**
   * Canonical field name. Display-title aliases point at this payload.
   * They do not own a second copy and are not merged across names.
   */
  name?: string;
}

/**
 * Question name or title → labels.
 * Rooms stored before column context used a flat value→text map. Readers
 * accept both shapes.
 */
export type SurveyQuestionLabels = QuestionChoiceLabels | Record<string, string>;

export interface SurveyChoiceLabelMap {
  [questionKey: string]: SurveyQuestionLabels;
}

interface SelectQuestion extends Question {
  choices?: Array<ItemValue>;
  visibleChoices?: Array<ItemValue>;
  enabledChoices?: Array<ItemValue>;
  choicesFromQuestion?: string;
  rateValues?: Array<ItemValue>;
  visibleRateValues?: Array<ItemValue>;
  rows?: Array<ItemValue>;
  columns?: Array<MatrixColumn>;
  templateElements?: Array<Question>;
  elements?: Array<Question>;
  questions?: Array<Question>;
  items?: Array<{ name?: string; title?: string }>;
  valueTrue?: unknown;
  valueFalse?: unknown;
  labelTrue?: string;
  labelFalse?: string;
  /** Present on choice questions. Not declared on the base `Question` type. */
  otherItem?: ItemValue;
  isOtherItem?: (item: ItemValue) => boolean;
}

interface MatrixColumn {
  name?: string;
  title?: string;
  templateQuestion?: Question;
}

/**
 * `ISurvey.getQuestionByName` is typed as `IQuestion`. Choice text is read
 * from the concrete question model SurveyJS builds (`Question` and its
 * subclasses). Anything else is left unlabeled.
 */
function isQuestionModel(value: unknown): value is Question {
  return value instanceof Question;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isStringRecord(value: unknown): value is Record<string, string> {
  if (!isRecord(value)) return false;
  return Object.values(value).every((entry) => typeof entry === 'string');
}

function plainText(value: string): string {
  return value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
}

function choiceValueKey(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function addLabel(map: Record<string, string>, value: unknown, text: unknown): void {
  const key = choiceValueKey(value);
  const label = typeof text === 'string' ? text.trim() : '';
  if (!key || !label || label === key) return;
  map[key] = label;
}

function firstNonEmpty(lists: Array<Array<ItemValue> | undefined>): Array<ItemValue> {
  for (const list of lists) {
    if (Array.isArray(list) && list.length > 0) return list;
  }
  return [];
}

function mergeItems(base: Array<ItemValue>, override: Array<ItemValue>): Array<ItemValue> {
  const byValue = new Map<string, ItemValue>();
  for (const item of base) {
    const key = choiceValueKey(item?.value);
    if (key) byValue.set(key, item);
  }
  for (const item of override) {
    const key = choiceValueKey(item?.value);
    if (key) byValue.set(key, item);
  }
  return Array.from(byValue.values());
}

/**
 * Authored choices plus the choices SurveyJS has already resolved
 * (locale text, choicesFromQuestion). Resolved text wins on a shared value.
 * An empty resolved list still keeps the source question's choices, so a
 * `selected` carry-forward with no answers yet does not drop every label.
 */
function choiceItems(question: Question, stack: Set<string>): Array<ItemValue> {
  const select = question as SelectQuestion;
  const name = question.name || '';
  if (name && stack.has(name)) return [];
  const next = new Set(stack);
  if (name) next.add(name);

  const authored = firstNonEmpty([select.choices, select.rateValues]);
  const resolved = firstNonEmpty([
    select.enabledChoices,
    select.visibleChoices,
    select.visibleRateValues,
  ]);
  const direct = mergeItems(authored, resolved);
  const sourceName = select.choicesFromQuestion;
  if (!sourceName || !question.survey) return direct;
  const source = question.survey.getQuestionByName(sourceName);
  if (!isQuestionModel(source) || source === question) return direct;
  return mergeItems(choiceItems(source, next), direct);
}

function indexKeys(question: Question): Array<string> {
  const keys: Array<string> = [];
  const push = (value: unknown) => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (!trimmed || keys.includes(trimmed)) return;
    keys.push(trimmed);
    const plain = plainText(trimmed);
    if (plain && !keys.includes(plain)) keys.push(plain);
  };
  push(question.name);
  push(question.title);
  const locTitle = question.locTitle;
  if (locTitle) {
    push(locTitle.getLocaleText(''));
    push(locTitle.getLocaleText('default'));
  }
  return keys;
}

function displayTitle(question: Question): string | undefined {
  const title = plainText(question.title || '');
  if (!title || title === question.name) return undefined;
  return title;
}

function hasChoiceContent(labels: QuestionChoiceLabels): boolean {
  return (
    Object.keys(labels.values).length > 0 ||
    !!labels.columns ||
    !!labels.nested
  );
}

function mergeStringMaps(
  left: Record<string, string> | undefined,
  right: Record<string, string> | undefined,
): Record<string, string> | undefined {
  if (!left && !right) return undefined;
  return { ...(left ?? {}), ...(right ?? {}) };
}

function mergeLabels(
  existing: QuestionChoiceLabels | undefined,
  incoming: QuestionChoiceLabels,
): QuestionChoiceLabels {
  if (!existing) return incoming;
  if (existing.name && incoming.name && existing.name !== incoming.name) {
    return existing;
  }
  const nested = mergeNested(existing.nested, incoming.nested);
  const columns = mergeStringMaps(existing.columns, incoming.columns);
  const title = incoming.title || existing.title;
  const name = incoming.name || existing.name;
  const isOpenText = existing.isOpenText === true || incoming.isOpenText === true;
  return {
    values: { ...existing.values, ...incoming.values },
    ...(columns ? { columns } : {}),
    ...(nested ? { nested } : {}),
    ...(title ? { title } : {}),
    ...(name ? { name } : {}),
    ...(isOpenText ? { isOpenText: true } : {}),
  };
}

function mergeNested(
  left: Record<string, QuestionChoiceLabels> | undefined,
  right: Record<string, QuestionChoiceLabels> | undefined,
): Record<string, QuestionChoiceLabels> | undefined {
  if (!left && !right) return undefined;
  const merged: Record<string, QuestionChoiceLabels> = { ...(left ?? {}) };
  for (const [key, value] of Object.entries(right ?? {})) {
    merged[key] = mergeLabels(merged[key], value);
  }
  return Object.keys(merged).length > 0 ? merged : undefined;
}

/**
 * Index one field by its name, then by display-title aliases.
 * The name owns the payload. An alias is skipped when that key is already
 * another field's name, and a name replaces an alias without merging the
 * other field's choices in.
 */
function indexOwned(
  into: Record<string, QuestionChoiceLabels>,
  canonicalName: string,
  aliases: Array<string>,
  labels: QuestionChoiceLabels,
): void {
  const owned: QuestionChoiceLabels = canonicalName
    ? { ...labels, name: canonicalName }
    : labels;
  if (canonicalName) {
    const existing = into[canonicalName];
    into[canonicalName] =
      existing?.name === canonicalName ? mergeLabels(existing, owned) : owned;
  }
  const payload = (canonicalName && into[canonicalName]) || owned;
  for (const alias of aliases) {
    if (!alias || alias === canonicalName) continue;
    const existing = into[alias];
    if (!existing || !existing.name || existing.name === payload.name) {
      into[alias] = payload;
    }
  }
}

function indexQuestion(
  into: Record<string, QuestionChoiceLabels>,
  question: Question,
  labels: QuestionChoiceLabels,
): void {
  const canonical = (question.name || '').trim();
  const aliases = indexKeys(question).filter((key) => key !== canonical);
  indexOwned(into, canonical, aliases, labels);
}

/** Free-text questions. Their answers are typed text, not choice codes. */
function isOpenTextType(type: string): boolean {
  return type === 'text' || type === 'comment' || type === 'multipletext';
}

function buildQuestionLabels(question: Question): QuestionChoiceLabels {
  const type = question.getType();
  const labels: QuestionChoiceLabels = { values: {} };
  if (isOpenTextType(type)) labels.isOpenText = true;
  const title = displayTitle(question);
  if (title) labels.title = title;

  if (!isOpenTextType(type)) {
    for (const item of choiceItems(question, new Set<string>())) {
      addLabel(labels.values, item.value, item.text);
    }
  }

  if (type === 'boolean') addBoolean(question, labels);
  if (type === 'multipletext') addMultipleText(question, labels);
  if (type === 'matrix') addMatrixGrid(question, labels);
  if (type === 'matrixdropdown' || type === 'matrixdynamic') {
    addMatrixNested(question, labels);
  }
  addOtherItem(question, labels);
  return labels;
}

/**
 * SurveyJS stores the "Other" comment as `{ [otherItem.value]: otherText }`.
 * That child is typed text. Its key is the other item's value, not a guess
 * from the answer object.
 */
function addOtherItem(question: Question, labels: QuestionChoiceLabels): void {
  if (question.showOtherItem !== true && question.hasOther !== true) return;
  const select = question as SelectQuestion;
  const item = select.otherItem;
  if (!item) return;
  if (typeof select.isOtherItem === 'function' && !select.isOtherItem(item)) return;
  const name = choiceValueKey(item.value);
  if (!name) return;
  const text = typeof item.text === 'string' ? plainText(item.text) : '';
  const child: QuestionChoiceLabels = { values: {}, isOpenText: true };
  if (text && text !== name) child.title = text;
  const nested: Record<string, QuestionChoiceLabels> = {};
  indexOwned(nested, name, [], child);
  labels.nested = mergeNested(labels.nested, nested);
}

function addBoolean(question: Question, labels: QuestionChoiceLabels): void {
  const select = question as SelectQuestion;
  addLabel(labels.values, select.valueTrue ?? true, select.labelTrue);
  addLabel(labels.values, select.valueFalse ?? false, select.labelFalse);
}

function addMultipleText(question: Question, labels: QuestionChoiceLabels): void {
  const items = (question as SelectQuestion).items;
  if (!Array.isArray(items)) return;
  const nested: Record<string, QuestionChoiceLabels> = {};
  for (const item of items) {
    if (!item || typeof item.name !== 'string') continue;
    const name = item.name.trim();
    if (!name) continue;
    const title = typeof item.title === 'string' ? plainText(item.title) : '';
    const child: QuestionChoiceLabels = { values: {}, isOpenText: true };
    if (title && title !== name) child.title = title;
    indexOwned(nested, name, title && title !== name ? [title] : [], child);
  }
  if (Object.keys(nested).length > 0) {
    labels.nested = mergeNested(labels.nested, nested);
  }
}

function addMatrixGrid(question: Question, labels: QuestionChoiceLabels): void {
  const select = question as SelectQuestion;
  const columns: Record<string, string> = {};
  if (Array.isArray(select.rows)) {
    for (const row of select.rows) addLabel(labels.values, row.value, row.text);
  }
  if (Array.isArray(select.columns)) {
    for (const column of select.columns) {
      if (!isItemColumn(column)) continue;
      addLabel(columns, column.value, column.text);
    }
  }
  if (Object.keys(columns).length > 0) labels.columns = columns;
}

function isItemColumn(column: MatrixColumn): column is MatrixColumn & ItemValue {
  return typeof (column as ItemValue).value !== 'undefined' && !column.templateQuestion;
}

function addMatrixNested(question: Question, labels: QuestionChoiceLabels): void {
  const select = question as SelectQuestion;
  if (Array.isArray(select.rows)) {
    for (const row of select.rows) addLabel(labels.values, row.value, row.text);
  }
  const nested: Record<string, QuestionChoiceLabels> = {};
  if (Array.isArray(select.columns)) {
    for (const column of select.columns) {
      const template = column.templateQuestion;
      if (!template || typeof template.getType !== 'function') continue;
      const child = buildQuestionLabels(template);
      const columnTitle = typeof column.title === 'string' ? plainText(column.title) : '';
      if (columnTitle && columnTitle !== (column.name || '')) child.title = columnTitle;
      const openText = isOpenTextType(template.getType());
      if (!hasChoiceContent(child) && !child.title && !openText) continue;
      const columnName = (column.name || '').trim();
      if (!columnName) continue;
      indexOwned(
        nested,
        columnName,
        columnTitle && columnTitle !== columnName ? [columnTitle] : [],
        child,
      );
    }
  }
  if (Object.keys(nested).length > 0) labels.nested = nested;
}

function panelChildren(element: Question): Array<Question> {
  const panel = element as SelectQuestion;
  const children = Array.isArray(panel.questions)
    ? panel.questions
    : Array.isArray(panel.elements)
      ? panel.elements
      : [];
  return children.filter((child) => child && typeof child.getType === 'function');
}

function templateQuestions(question: Question): Array<Question> {
  const elements = (question as SelectQuestion).templateElements;
  if (!Array.isArray(elements)) return [];
  const found: Array<Question> = [];
  const walk = (element: Question) => {
    if (element.getType() === 'panel') {
      for (const child of panelChildren(element)) walk(child);
      return;
    }
    found.push(element);
  };
  for (const element of elements) {
    if (element && typeof element.getType === 'function') walk(element);
  }
  return found;
}

function absorbDynamicPanel(
  question: Question,
  labels: QuestionChoiceLabels,
  into: Record<string, QuestionChoiceLabels>,
): void {
  const nested: Record<string, QuestionChoiceLabels> = {};
  for (const child of templateQuestions(question)) {
    const childLabels = buildQuestionLabels(child);
    if (child.getType() === 'paneldynamic') {
      absorbDynamicPanel(child, childLabels, into);
    }
    if (!hasChoiceContent(childLabels) && !isOpenTextType(child.getType())) continue;
    indexQuestion(into, child, childLabels);
    indexQuestion(nested, child, childLabels);
  }
  if (Object.keys(nested).length > 0) {
    labels.nested = mergeNested(labels.nested, nested);
  }
}

/**
 * Drop `choicesByUrl` so model construction does not open a request.
 * Unresolved remote choices stay unlabeled; the panel shows the raw value.
 */
function surveyJsonWithoutRemoteChoices(survey: unknown): unknown {
  const clone = JSON.parse(JSON.stringify(survey)) as unknown;
  stripChoicesByUrl(clone);
  return clone;
}

function stripChoicesByUrl(node: unknown): void {
  if (Array.isArray(node)) {
    for (const item of node) stripChoicesByUrl(item);
    return;
  }
  if (!isRecord(node)) return;
  if ('choicesByUrl' in node) delete node.choicesByUrl;
  for (const value of Object.values(node)) stripChoicesByUrl(value);
}

function isStructuredLabels(raw: Record<string, unknown>): boolean {
  return isStringRecord(raw.values);
}

/**
 * Accept the structured map and the older flat value→text map.
 */
export function normalizeSurveyQuestionLabels(raw: unknown): QuestionChoiceLabels | undefined {
  if (!isRecord(raw)) return undefined;
  if (isStructuredLabels(raw)) {
    const nested: Record<string, QuestionChoiceLabels> = {};
    if (isRecord(raw.nested)) {
      for (const [key, value] of Object.entries(raw.nested)) {
        const child = normalizeSurveyQuestionLabels(value);
        if (child) nested[key] = child;
      }
    }
    const title = typeof raw.title === 'string' ? raw.title.trim() : '';
    const name = typeof raw.name === 'string' ? raw.name.trim() : '';
    const isOpenText = raw.isOpenText === true;
    return {
      values: raw.values as Record<string, string>,
      ...(isStringRecord(raw.columns) ? { columns: raw.columns } : {}),
      ...(Object.keys(nested).length > 0 ? { nested } : {}),
      ...(title ? { title } : {}),
      ...(name ? { name } : {}),
      ...(isOpenText ? { isOpenText: true } : {}),
    };
  }
  if (isStringRecord(raw) && Object.keys(raw).length > 0) {
    return { values: raw };
  }
  return undefined;
}

/**
 * Value → authored text for every choice SurveyJS can resolve without a
 * network call. Empty when the survey has no labels that differ from values.
 */
export function buildSurveyChoiceLabels(survey: unknown): SurveyChoiceLabelMap {
  if (!isRecord(survey)) return {};
  try {
    const json = surveyJsonWithoutRemoteChoices(survey);
    const model = new Model(json);
    const locale = survey.locale;
    if (typeof locale === 'string' && locale && model.locale !== locale) {
      model.locale = locale;
    }
    const into: Record<string, QuestionChoiceLabels> = {};
    for (const question of model.getAllQuestions(false)) {
      const labels = buildQuestionLabels(question);
      if (question.getType() === 'paneldynamic') {
        absorbDynamicPanel(question, labels, into);
      }
      if (!hasChoiceContent(labels) && !isOpenTextType(question.getType())) continue;
      indexQuestion(into, question, labels);
    }
    return into;
  } catch {
    return {};
  }
}
